import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, LineChart, Line
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const COLORS = ['#6B8E23', '#228B22', '#32CD32', '#90EE90', '#FFD700', '#FF8C00', '#FF6347', '#4682B4', '#9370DB', '#20B2AA'];
const ARCA_GREEN = '#6B8E23';

const SurveyAnalyticsDashboard = ({ onClose }) => {
    const [analytics, setAnalytics] = useState(null);
    const [baseAnalytics, setBaseAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        country: '',
        profession: '',
        yearsOfExperience: '',
        organization: '',
        studentProfessional: '',
        startDate: '',
        endDate: '',
        search: ''
    });
    const [activeTab, setActiveTab] = useState('overview');

    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        'Content-Type': 'application/json'
    };

    useEffect(() => {
        fetchAnalytics({});
    }, []);

    const fetchAnalytics = async (appliedFilters = {}) => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            Object.entries(appliedFilters).forEach(([key, value]) => {
                if (value) params.append(key, value);
            });
            const query = params.toString() ? `?${params.toString()}` : '';
            const res = await axios.get(`${API_URL}/survey/analytics${query}`, { headers });
            if (res.data.success) {
                setAnalytics(res.data.data);
                // Keep the first (unfiltered) load as the source for filter options
                setBaseAnalytics(prev => prev || res.data.data);
            }
        } catch (err) {
            toast.error('Failed to load analytics dashboard');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => fetchAnalytics(filters);

    const clearFilters = () => {
        const cleared = { country: '', profession: '', yearsOfExperience: '', organization: '', studentProfessional: '', startDate: '', endDate: '', search: '' };
        setFilters(cleared);
        fetchAnalytics(cleared);
    };

    const hasActiveFilters = useMemo(() => Object.values(filters).some(v => v), [filters]);

    const exportAnalyticsCSV = () => {
        if (!analytics) return;
        const sections = [
            ['Metric', 'Value'],
            ['Total Responses', analytics.total],
            ['Response Rate', `${analytics.responseRate}%`],
            ['Countries Represented', analytics.byCountry.length],
            ['Professions Represented', analytics.byProfession.length],
            [],
            ['Country', 'Count'],
            ...analytics.byCountry.map(r => [r.country, r.count]),
            [],
            ['Profession', 'Count'],
            ...analytics.byProfession.map(r => [r.profession, r.count]),
            [],
            ['Interest Level', 'Count'],
            ...analytics.byInterest.map(r => [r.interest_attending || 'Not specified', r.count]),
            [],
            ['Preferred Topic', 'Count'],
            ...analytics.topicCounts.map(r => [r.name, r.count]),
            [],
            ['Preferred Format', 'Count'],
            ...analytics.formatCounts.map(r => [r.name, r.count])
        ];
        const csv = sections.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `arca2027-survey-analytics-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Analytics CSV exported');
    };

    const exportChart = (chartId) => {
        const svg = document.querySelector(`#${chartId} svg`);
        if (!svg) return;
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
            canvas.width = img.width * 2;
            canvas.height = img.height * 2;
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const link = document.createElement('a');
            link.download = `${chartId}-${Date.now()}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        };
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
    };

    // Compute AI-style insights from the (filtered) analytics
    const insights = useMemo(() => {
        if (!analytics) return [];
        const { total, byInterest, byCountry, byProfession, byStudentProfessional, byOrganization, topicCounts, formatCounts, byParticipationMode, networkingCounts, byFee } = analytics;
        const list = [];

        if (byInterest.length) {
            const interestedCount = byInterest.filter(i => (i.interest_attending || '').toLowerCase().includes('definitely') || (i.interest_attending || '').toLowerCase().includes('probably')).reduce((s, i) => s + i.count, 0);
            const interestPct = total ? Math.round((interestedCount / total) * 100) : 0;
            const topInterest = byInterest.sort((a, b) => b.count - a.count)[0];
            list.push({
                title: 'Attendance Interest',
                text: `${interestPct}% of respondents (${interestedCount} of ${total}) expressed interest in attending. The most common response was "${topInterest.interest_attending || 'Not specified'}" with ${topInterest.count} responses. This indicates strong early demand for the conference.`
            });
        }

        if (byCountry.length) {
            const topCountry = byCountry[0];
            list.push({
                title: 'Geographic Reach',
                text: `Responses came from ${byCountry.length} countries. ${topCountry.country} leads with ${topCountry.count} responses (${Math.round((topCountry.count / total) * 100)}% of total), showing a strong base there. Consider targeted outreach in underrepresented regions.`
            });
        }

        if (topicCounts.length) {
            const topTopic = topicCounts[0];
            list.push({
                title: 'Most Requested Topics',
                text: `The most requested topic is "${topTopic.name}" (${topTopic.count} selections). Top themes include: ${topicCounts.slice(0, 3).map(t => t.name).join(', ')}. These should be prioritized in the programme.`
            });
        }

        if (formatCounts.length) {
            const topFormat = formatCounts[0];
            list.push({
                title: 'Preferred Formats',
                text: `Respondents prefer "${topFormat.name}" as the leading session format (${topFormat.count} selections). A blend of ${formatCounts.slice(0, 3).map(f => f.name).join(', ')} is recommended.`
            });
        }

        if (byProfession.length) {
            const topProfession = byProfession[0];
            list.push({
                title: 'Professional Audience',
                text: `${topProfession.profession} is the largest professional group (${topProfession.count} responses). Content should be tailored to this audience while remaining inclusive of other disciplines.`
            });
        }

        if (byStudentProfessional.length) {
            const studentCount = byStudentProfessional.find(s => s.student_professional === 'Student')?.count || 0;
            const professionalCount = byStudentProfessional.find(s => s.student_professional === 'Professional')?.count || 0;
            const studentPct = total ? Math.round((studentCount / total) * 100) : 0;
            list.push({
                title: 'Student vs Professional Split',
                text: `${studentPct}% of respondents identified as students (${studentCount}), while ${professionalCount} identified as professionals. Consider targeted student pricing, mentorship sessions, and career-development tracks.`
            });
        }

        if (byOrganization.length) {
            const topOrg = byOrganization[0];
            list.push({
                title: 'Institutional Representation',
                text: `${topOrg.organization} is the most represented organization (${topOrg.count} responses). Institutional partnerships and group-registration offers could help drive attendance.`
            });
        }

        if (networkingCounts.length) {
            const topNetworking = networkingCounts[0];
            list.push({
                title: 'Networking Priorities',
                text: `The preferred networking approach is "${topNetworking.name}" (${topNetworking.count} selections). Build structured networking sessions around this format.`
            });
        }

        if (byFee.length) {
            const feeMap = Object.fromEntries(byFee.map(f => [f.reasonable_fee, f.count]));
            const affordable = (feeMap['Under $100'] || 0) + (feeMap['$100 - $200'] || 0);
            list.push({
                title: 'Pricing Sensitivity',
                text: `${affordable} respondents indicated fees under $200 are reasonable. Use this as a pricing anchor and offer early-bird or student discounts to maximize attendance.`
            });
        }

        return list;
    }, [analytics]);

    const recommendations = useMemo(() => {
        if (!analytics) return [];
        const recs = [];
        const { byInterest, byCountry, topicCounts, formatCounts, byParticipationMode, networkingCounts, byFee, priorityCounts, byStudentProfessional, byOrganization } = analytics;

        if (byInterest.length) {
            const interested = byInterest.filter(i => (i.interest_attending || '').toLowerCase().includes('definitely')).reduce((s, i) => s + i.count, 0);
            if (interested > 0) recs.push('Launch an early-bird registration for definitely interested respondents to secure commitments.');
        }
        if (byCountry.length > 1) {
            recs.push(`Engage country ambassadors in ${byCountry.slice(0, 3).map(c => c.country).join(', ')} to drive local registration.`);
        }
        if (topicCounts.length) {
            recs.push(`Prioritize sessions on ${topicCounts.slice(0, 3).map(t => t.name).join(', ')} in the main programme.`);
        }
        if (formatCounts.length) {
            recs.push(`Design the agenda around ${formatCounts.slice(0, 2).map(f => f.name).join(' and ')} formats.`);
        }
        if (byParticipationMode.length) {
            const hybrid = byParticipationMode.find(m => (m.participation_mode || '').toLowerCase().includes('hybrid'));
            const virtual = byParticipationMode.find(m => (m.participation_mode || '').toLowerCase().includes('virtual'));
            if (hybrid || virtual) recs.push('Offer a hybrid or virtual participation option to accommodate remote attendees.');
        }
        if (networkingCounts.length) {
            recs.push(`Schedule dedicated ${networkingCounts[0].name.toLowerCase()} networking sessions.`);
        }
        if (byFee.length) {
            recs.push('Introduce tiered pricing (early bird, student, member, non-member) aligned with the fee sensitivity data.');
        }
        if (priorityCounts.length) {
            recs.push(`Focus ARCA priorities on ${priorityCounts.slice(0, 3).map(p => p.name).join(', ')}.`);
        }
        if (byStudentProfessional.length) {
            const studentCount = byStudentProfessional.find(s => s.student_professional === 'Student')?.count || 0;
            if (studentCount > 0) recs.push('Promote a discounted student registration tier and student-specific networking sessions.');
        }
        if (byOrganization.length > 1) {
            recs.push(`Reach out to top represented organizations for group bookings and institutional sponsorships.`);
        }
        return recs.slice(0, 8);
    }, [analytics]);

    // Destructure analytics safely and prepare filter options BEFORE any early returns
    // (React requires all hooks to run before any conditional return.)
    const {
        total = 0,
        baseTotal = total,
        responseRate = 0,
        byCountry = [],
        byProfession = [],
        byOrganization = [],
        byStudentProfessional = [],
        byInterest = [],
        byPracticeArea = [],
        byFee = [],
        byExperience = [],
        byParticipationMode = [],
        topicCounts = [],
        formatCounts = [],
        networkingCounts = [],
        exhibitorCounts = [],
        attendanceFactorCounts = [],
        priorityCounts = [],
        motivationCounts = [],
        heardAboutCounts = [],
        timeline = [],
        recentComments = []
    } = analytics || {};

    const filterOptions = useMemo(() => ({
        byCountry: [],
        byProfession: [],
        byExperience: [],
        byOrganization: [],
        ...baseAnalytics,
        ...analytics
    }), [baseAnalytics, analytics]);

    if (loading && !analytics) {
        return (
            <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
                <div className="modal-dialog modal-fullscreen modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-body text-center py-5">
                            <div className="spinner-border text-success" role="status"></div>
                            <p className="mt-3 text-muted">Loading analytics dashboard...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!analytics) {
        return (
            <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
                <div className="modal-dialog modal-fullscreen modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">ARCA 2027 Survey Analytics</h5>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body text-center py-5 text-muted">
                            <i className="bi bi-bar-chart-line fs-1 d-block mb-3"></i>
                            <p>Unable to load analytics.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Breadcrumb / Header */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
                <div>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-1">
                            <li className="breadcrumb-item">
                                <button className="btn btn-link p-0 text-decoration-none" onClick={onClose}>ARCA 2027 Responses</button>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">Analytics</li>
                        </ol>
                    </nav>
                    <h4 className="mb-0 fw-bold text-dark">
                        <i className="bi bi-bar-chart-line-fill me-2 text-success"></i>
                        ARCA 2027 Survey Analytics
                    </h4>
                    <small className="text-muted">Comprehensive analysis of conference survey responses</small>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-success" onClick={exportAnalyticsCSV}>
                        <i className="bi bi-download me-2"></i>Export CSV
                    </button>
                    <button className="btn btn-outline-secondary" onClick={onClose}>
                        <i className="bi bi-arrow-left me-1"></i>Back to Responses
                    </button>
                </div>
            </div>
            {/* Metric Cards */}
            <div className="row g-3 mb-4">
                {[
                    { icon: 'bi-people', label: 'Total Responses', value: total, color: ARCA_GREEN },
                    { icon: 'bi-graph-up', label: 'Response Rate', value: `${responseRate}%`, color: '#228B22' },
                    { icon: 'bi-globe', label: 'Countries', value: byCountry.length, color: '#4682B4' },
                    { icon: 'bi-briefcase', label: 'Professions', value: byProfession.length, color: '#9370DB' },
                    { icon: 'bi-building', label: 'Organizations', value: byOrganization.length, color: '#FF8C00' }
                ].map((card, idx) => (
                    <div key={idx} className="col-xl col-md-4 col-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-body d-flex align-items-center">
                                <div className="rounded-circle p-3 me-3" style={{ backgroundColor: `${card.color}15` }}>
                                    <i className={`bi ${card.icon} fs-4`} style={{ color: card.color }}></i>
                                </div>
                                <div>
                                    <h3 className="mb-0 fw-bold" style={{ color: card.color }}>{card.value}</h3>
                                    <small className="text-muted">{card.label}</small>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small text-muted mb-1">Country</label>
                            <select className="form-select form-select-sm" value={filters.country} onChange={(e) => setFilters(f => ({ ...f, country: e.target.value }))}>
                                <option value="">All Countries</option>
                                {filterOptions.byCountry.map(c => <option key={c.country} value={c.country}>{c.country}</option>)}
                            </select>
                        </div>
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small text-muted mb-1">Profession</label>
                            <select className="form-select form-select-sm" value={filters.profession} onChange={(e) => setFilters(f => ({ ...f, profession: e.target.value }))}>
                                <option value="">All Professions</option>
                                {filterOptions.byProfession.map(p => <option key={p.profession} value={p.profession}>{p.profession}</option>)}
                            </select>
                        </div>
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small text-muted mb-1">Years of Experience</label>
                            <select className="form-select form-select-sm" value={filters.yearsOfExperience} onChange={(e) => setFilters(f => ({ ...f, yearsOfExperience: e.target.value }))}>
                                <option value="">All</option>
                                {filterOptions.byExperience.map(y => <option key={y.years_of_experience} value={y.years_of_experience}>{y.years_of_experience}</option>)}
                            </select>
                        </div>
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small text-muted mb-1">Organization</label>
                            <select className="form-select form-select-sm" value={filters.organization} onChange={(e) => setFilters(f => ({ ...f, organization: e.target.value }))}>
                                <option value="">All</option>
                                {filterOptions.byOrganization.map(o => <option key={o.organization} value={o.organization}>{o.organization}</option>)}
                            </select>
                        </div>
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small text-muted mb-1">Student / Professional</label>
                            <select className="form-select form-select-sm" value={filters.studentProfessional} onChange={(e) => setFilters(f => ({ ...f, studentProfessional: e.target.value }))}>
                                <option value="">All</option>
                                <option value="Student">Student</option>
                                <option value="Professional">Professional</option>
                            </select>
                        </div>
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small text-muted mb-1">Start Date</label>
                            <input type="date" className="form-control form-control-sm" value={filters.startDate} onChange={(e) => setFilters(f => ({ ...f, startDate: e.target.value }))} />
                        </div>
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small text-muted mb-1">End Date</label>
                            <input type="date" className="form-control form-control-sm" value={filters.endDate} onChange={(e) => setFilters(f => ({ ...f, endDate: e.target.value }))} />
                        </div>
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small text-muted mb-1">Search</label>
                            <input type="text" className="form-control form-control-sm" placeholder="Keyword..." value={filters.search} onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} />
                        </div>
                        <div className="col-12 d-flex gap-2">
                            <button className="btn btn-success btn-sm" onClick={applyFilters} disabled={loading}>
                                <i className="bi bi-funnel me-1"></i>{loading ? 'Loading...' : 'Apply Filters'}
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={clearFilters} disabled={loading}>
                                <i className="bi bi-x-circle me-1"></i>Clear
                            </button>
                            {hasActiveFilters && (
                                <span className="badge bg-warning text-dark d-flex align-items-center">
                                    <i className="bi bi-funnel-fill me-1"></i>Showing {total} of {baseTotal} responses
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-2">
                    <ul className="nav nav-pills d-flex flex-row flex-wrap gap-2">
                        {[
                            { id: 'overview', label: 'Overview', icon: 'bi-grid' },
                            { id: 'demographics', label: 'Demographics', icon: 'bi-person-vcard' },
                            { id: 'programme', label: 'Programme Preferences', icon: 'bi-calendar-event' },
                            { id: 'feedback', label: 'Open-Ended Feedback', icon: 'bi-chat-left-text' },
                            { id: 'insights', label: 'AI Insights', icon: 'bi-lightbulb' }
                        ].map(tab => (
                            <li key={tab.id} className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === tab.id ? 'active bg-success text-white' : 'text-dark'}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <i className={`bi ${tab.icon} me-1`}></i>{tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <div className="row g-4">
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100" id="chart-interest">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold">Attendance Interest</h6>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => exportChart('chart-interest')}>
                                    <i className="bi bi-image"></i>
                                </button>
                            </div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={byInterest.map(i => ({ name: i.interest_attending || 'Not specified', value: i.count }))}
                                            cx="50%"
                                            cy="45%"
                                            outerRadius={90}
                                            dataKey="value"
                                            nameKey="name"
                                            labelLine
                                            label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                                        >
                                            {byInterest.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(value, name) => [`${value} responses`, name]} />
                                        <Legend verticalAlign="bottom" height={36} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100" id="chart-timeline">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold">Responses Over Time</h6>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => exportChart('chart-timeline')}>
                                    <i className="bi bi-image"></i>
                                </button>
                            </div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={timeline.map(t => ({ date: t.date, count: t.count }))}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} />
                                        <YAxis />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="count" stroke={ARCA_GREEN} strokeWidth={2} dot={{ r: 4 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100" id="chart-countries">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold">Top Countries</h6>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => exportChart('chart-countries')}>
                                    <i className="bi bi-image"></i>
                                </button>
                            </div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={byCountry.slice(0, 10)} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="country" width={100} tick={{ fontSize: 12 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill={ARCA_GREEN} radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100" id="chart-professions">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-bold">Professions</h6>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => exportChart('chart-professions')}>
                                    <i className="bi bi-image"></i>
                                </button>
                            </div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={byProfession.slice(0, 8)}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="profession" angle={-30} textAnchor="end" height={80} tick={{ fontSize: 11 }} />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#4682B4" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Demographics Tab */}
            {activeTab === 'demographics' && (
                <div className="row g-4">
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">Years of Experience</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={byExperience}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="years_of_experience" tick={{ fontSize: 11 }} />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="count" fill={ARCA_GREEN} radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">Participation Mode Preference</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={byParticipationMode.map(m => ({ name: m.participation_mode || 'Not specified', value: m.count }))}
                                            cx="50%"
                                            cy="45%"
                                            outerRadius={90}
                                            dataKey="value"
                                            nameKey="name"
                                            labelLine
                                            label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                                        >
                                            {byParticipationMode.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(value, name) => [`${value} responses`, name]} />
                                        <Legend verticalAlign="bottom" height={36} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">Primary Practice Area</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={byPracticeArea.slice(0, 8)} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="primary_practice_area" width={120} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#9370DB" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">Reasonable Fee Range</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={byFee}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="reasonable_fee" tick={{ fontSize: 11 }} />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#FF8C00" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">Organization / Institution</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={byOrganization.slice(0, 10)} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="organization" width={140} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#FF8C00" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">Student vs Professional</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={byStudentProfessional.map(s => ({ name: s.student_professional || 'Not specified', value: s.count }))}
                                            cx="50%"
                                            cy="45%"
                                            outerRadius={90}
                                            dataKey="value"
                                            nameKey="name"
                                            labelLine
                                            label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                                        >
                                            {byStudentProfessional.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(value, name) => [`${value} responses`, name]} />
                                        <Legend verticalAlign="bottom" height={36} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Programme Tab */}
            {activeTab === 'programme' && (
                <div className="row g-4">
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">Preferred Topics</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={topicCounts.slice(0, 10)} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill={ARCA_GREEN} radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">Preferred Session Formats</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={350}>
                                    <BarChart data={formatCounts.slice(0, 10)} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#228B22" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">Networking Preferences</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={networkingCounts.slice(0, 8)} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#4682B4" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">Top Attendance Factors</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={attendanceFactorCounts.slice(0, 8)} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#FF6347" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Feedback Tab */}
            {activeTab === 'feedback' && (
                <div className="row g-4">
                    <div className="col-12">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">Recent Open-Ended Responses</h6></div>
                            <div className="card-body">
                                {recentComments.length === 0 ? (
                                    <p className="text-muted mb-0">No open-ended responses yet.</p>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Biggest Challenge</th>
                                                    <th>Unique Feature</th>
                                                    <th>Additional Comments</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentComments.slice(0, 20).map((c, idx) => (
                                                    <tr key={idx}>
                                                        <td>{c.submitted_at ? new Date(c.submitted_at).toLocaleDateString() : '-'}</td>
                                                        <td>{c.biggest_challenge || '-'}</td>
                                                        <td>{c.unique_feature || '-'}</td>
                                                        <td>{c.additional_comments || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">ARCA Priorities</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={priorityCounts.slice(0, 8)} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill={ARCA_GREEN} radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm h-100">
                            <div className="card-header bg-white"><h6 className="mb-0 fw-bold">How Respondents Heard About ARCA</h6></div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={heardAboutCounts.slice(0, 8)} layout="vertical">
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#20B2AA" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Insights Tab */}
            {activeTab === 'insights' && (
                <div className="row g-4">
                    <div className="col-lg-8">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white d-flex align-items-center">
                                <i className="bi bi-lightbulb text-warning me-2"></i>
                                <h6 className="mb-0 fw-bold">Key Findings & AI-Generated Insights</h6>
                            </div>
                            <div className="card-body">
                                {insights.map((insight, idx) => (
                                    <div key={idx} className="mb-4 pb-3 border-bottom">
                                        <h6 className="fw-bold text-success">{insight.title}</h6>
                                        <p className="mb-0 text-muted">{insight.text}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4">
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white d-flex align-items-center">
                                <i className="bi bi-stars text-success me-2"></i>
                                <h6 className="mb-0 fw-bold">AI Recommendations</h6>
                            </div>
                            <div className="card-body">
                                <ul className="list-group list-group-flush">
                                    {recommendations.map((rec, idx) => (
                                        <li key={idx} className="list-group-item ps-0">
                                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                                            {rec}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SurveyAnalyticsDashboard;
